import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { getAuthorizeUrl, completeLogin, validateSession, endSession, collectNativeSession, setCredentials, clearCredentials, setUnauthorizedHandler, renewToken as renewTokenRequest, localAdminLogin } from "../lib/sailpoint";
import { saveReturnTo, currentDeepLinkTarget } from "../lib/deepLink";

// SailPoint refuses to redirect to a non-HTTPS scheme — it authenticates and
// then renders a dead-end "Successfully Authenticated!" page — so the native
// app cannot use com.kccs.identitysecurity:// as its redirect_uri. It signs in
// through the hosted web callback instead, which then hands the session off to
// the app via the custom scheme.
const NATIVE_WEB_REDIRECT_URI = "https://adminstudio.vercel.app/auth/callback";

const AuthContext = createContext(null);

// sessionStorage (not localStorage) — cleared when the browser/tab closes,
// but — unlike a pure in-memory store — carried over when a link with
// target="_blank" opens a new tab (browsers clone sessionStorage from the
// opener), so "open in new tab" links land already signed in instead of
// bouncing through the login screen.
//
// Only the tenant and an opaque session id are kept here. The password is
// never written to storage — it's posted once at sign-in and exchanged
// server-side for a token that never leaves the server.
const SESSION_KEY = "sp_session";

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null); // { tenant, identity } when logged in
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(true);
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();

  // Collects a native login once the browser sheet has completed it. Gives up
  // after a few minutes so an abandoned sign-in doesn't poll forever.
  const pollForNativeSession = useCallback(async (state) => {
    const deadline = Date.now() + 3 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2000));
      let sessionId = null;
      try {
        sessionId = await collectNativeSession(state);
      } catch {
        continue; // transient — keep trying until the deadline
      }
      if (!sessionId) continue;

      try {
        await Browser.close();
      } catch {
        // Sheet already dismissed by the user.
      }
      const { tenant, identity, strongAuth, claims, elevated } = await validateSession(sessionId);
      setCredentials({ tenant, sessionId, identityId: identity?.id });
      setSession({ tenant, identity, strongAuth, claims, elevated });
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ tenant, sessionId }));
      setLoading(false);
      return;
    }
    setLoading(false);
    setError("Sign-in timed out. Please try again.");
  }, []);

  // Step 1 — hand off to SailPoint's hosted login page.
  const beginLogin = useCallback(async (tenant) => {
    setLoading(true);
    setError(null);
    const native = Capacitor.isNativePlatform();
    try {
      const redirectUri = native
        ? NATIVE_WEB_REDIRECT_URI
        : `${window.location.origin}/auth/callback`;
      const { url, state } = await getAuthorizeUrl(tenant, redirectUri, native);

      if (native) {
        // Open in a system browser sheet rather than navigating the WebView —
        // the WebView *is* the app, so navigating it away would unload it.
        //
        // presentationStyle "popover" + width/height only has an effect on
        // iPad — SFSafariViewController (what this plugin wraps on iOS) is
        // always full-screen on iPhone, with no OS-level way to shrink it.
        // Sized at 80% of the screen so it visually reads as "a window", not
        // the app itself, making it more obvious to close after signing in.
        await Browser.open({
          url,
          presentationStyle: "popover",
          width: Math.round(window.screen.width * 0.8),
          height: Math.round(window.screen.height * 0.8),
        });
        // iOS sandboxes that sheet: it cannot open our custom URL scheme, so
        // the session can't be pushed back to us. Poll for it instead, using
        // the state this login started with.
        pollForNativeSession(state);
      } else {
        // Deep link: come back to the page that was asked for, not Home.
        saveReturnTo(currentDeepLinkTarget());
        // On web we're leaving the page entirely, so loading stays true.
        window.location.assign(url);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Could not start sign-in.";
      setError(msg);
      setLoading(false);
      throw new Error(msg);
    }
  }, [pollForNativeSession]);

  // Username/password sign-in for the server's local admin account. No
  // SailPoint involved: the credentials are checked against the server's own
  // environment, and the session that comes back can only manage the OAuth
  // client registry.
  const beginLocalLogin = useCallback(async (username, password) => {
    setLoading(true);
    setError(null);
    try {
      const { sessionId, identity } = await localAdminLogin(username, password);
      setCredentials({ tenant: null, sessionId });
      setSession({ tenant: null, identity, localAdmin: true });
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ tenant: null, sessionId }));
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Sign-in failed.";
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  // Step 2 — we've been redirected back with a code; trade it for a session.
  const finishLogin = useCallback(async (code, state) => {
    setLoading(true);
    setError(null);
    try {
      const { tenant, sessionId, identity, strongAuth, claims, elevated, native } =
        await completeLogin(code, state);
      setCredentials({ tenant, sessionId, identityId: identity?.id });
      setSession({ tenant, identity, strongAuth, claims, elevated });
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ tenant, sessionId }));
      // The caller needs to know whether to hand off to the native app.
      return { native: !!native, sessionId };
    } catch (err) {
      clearCredentials();
      sessionStorage.removeItem(SESSION_KEY);
      const msg = err.response?.data?.error || err.message || "Sign-in failed.";
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  // On mount (including a freshly opened tab that inherited sessionStorage
  // from its opener), silently restore a previous session if its id is still
  // live server-side. Server restarts drop sessions, so this can legitimately
  // fail — in which case we just fall back to the login screen.
  useEffect(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (!saved) {
      setRestoring(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { tenant, sessionId } = JSON.parse(saved);
        setCredentials({ tenant, sessionId });
        const { identity, strongAuth, claims, elevated, localAdmin } = await validateSession(sessionId);
        // Re-set with the identity id so owner-scoped calls (approvals) work
        // after a restore, not just after a fresh sign-in.
        setCredentials({ tenant, sessionId, identityId: identity?.id });
        if (!cancelled) setSession({ tenant, identity, strongAuth, claims, elevated, localAdmin: localAdmin === true });
      } catch {
        clearCredentials();
        sessionStorage.removeItem(SESSION_KEY);
      } finally {
        if (!cancelled) setRestoring(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Native sign-in completes in the web callback, which hands the session id
  // back through the custom URL scheme. Adopt it rather than exchanging a
  // code again — the code is single-use and already spent.
  const adoptSession = useCallback(async (sessionId) => {
    setLoading(true);
    setError(null);
    try {
      const { tenant, identity, strongAuth, claims, elevated } = await validateSession(sessionId);
      setCredentials({ tenant, sessionId, identityId: identity?.id });
      setSession({ tenant, identity, strongAuth, claims, elevated });
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ tenant, sessionId }));
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Sign-in failed.";
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  // The server drops sessions on restart; when that happens mid-use, land the
  // user back on the login screen with an explanation rather than 401s.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      sessionStorage.removeItem(SESSION_KEY);
      clearCredentials();
      setSession(null);
      setError("Your session expired. Please sign in again.");
      queryClient.clear();
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Forces a fresh access token now rather than waiting for the current one
  // to expire — the Profile screen's "Renew Token" button, useful right
  // after an admin grants the OAuth client a new scope/authority server-side,
  // since the still-valid cached token has no way to pick that up on its own.
  const renewToken = useCallback(async () => {
    const { tenant, identity, strongAuth, claims, elevated } = await renewTokenRequest();
    setSession((prev) => ({ ...prev, tenant, identity, strongAuth, claims, elevated }));
    return { strongAuth, claims, elevated };
  }, []);

  const logout = useCallback(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      try {
        endSession(JSON.parse(saved).sessionId);
      } catch {
        // Malformed stored session — nothing to revoke.
      }
    }
    clearCredentials();
    sessionStorage.removeItem(SESSION_KEY);
    setSession(null);
    setError(null);
    // Every cached query (identities, roles, scans, this user's own
    // preferences, everything) is scoped to whoever was signed in when it
    // was fetched — wiped here so a different tenant or user signing into
    // this same browser next never has a stale cached value flash before
    // its own fetch lands, let alone risk seeing another tenant's data.
    queryClient.clear();
  }, [queryClient]);

  return (
    <AuthContext.Provider value={{ session, loading, restoring, error, beginLogin, beginLocalLogin, finishLogin, adoptSession, logout, renewToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
